import React, { useEffect, useRef, useState } from "react";
import { Button, Form, ListGroup } from "react-bootstrap";
import { MapPin, Search, X } from "lucide-react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

/**
 * Where a location is, as one point on a map.
 *
 * GIS search uses it to take people to a location that has no georeferenced
 * maps yet (one with maps is fitted to its sheets instead). Optional: a
 * location without a pin still works, it just can't be found that way.
 *
 * The pin can be set by clicking the map, dragging the marker, finding a place
 * by name (OpenStreetMap's geocoder, as the georeference screen uses), or
 * typing / pasting "lat, lng" — the format Google Maps copies.
 *
 * value: { latitude, longitude } or null; onChange gets the same.
 */

const STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";
const GUJARAT = { center: [71.6, 22.4], zoom: 5.6 };
const PIN_ZOOM = 11;
const MARKER_COLOR = "#9F2F48";

const round6 = (n) => Math.round(n * 1e6) / 1e6;
const format = (pin) => (pin ? `${pin.latitude}, ${pin.longitude}` : "");

/** "22.30, 70.80" (or with a space only) -> { latitude, longitude }, else null. */
function parseCoordinates(text) {
  const match = String(text)
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const latitude = Number(match[1]);
  const longitude = Number(match[2]);
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude: round6(latitude), longitude: round6(longitude) };
}

export default function LocationPinPicker({ value, onChange, disabled = false, searchHint = "" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [coordsText, setCoordsText] = useState(format(value));
  const [coordsError, setCoordsError] = useState("");
  const [query, setQuery] = useState(searchHint);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  // The map, once. Clicks set the pin; the modal animates in, so it is
  // resized once it has its final size.
  useEffect(() => {
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      center: value ? [value.longitude, value.latitude] : GUJARAT.center,
      zoom: value ? PIN_ZOOM : GUJARAT.zoom,
      dragRotate: false,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    if (!disabled) {
      map.getCanvas().style.cursor = "crosshair";
      map.on("click", (e) => {
        onChangeRef.current({ latitude: round6(e.lngLat.lat), longitude: round6(e.lngLat.lng) });
      });
    }
    mapRef.current = map;
    const timer = setTimeout(() => map.resize(), 250);

    return () => {
      clearTimeout(timer);
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The marker follows the value.
  useEffect(() => {
    setCoordsText(format(value));
    setCoordsError("");
    const map = mapRef.current;
    if (!map) return;

    if (!value) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    const lngLat = [value.longitude, value.latitude];
    if (!markerRef.current) {
      markerRef.current = new maplibregl.Marker({ color: MARKER_COLOR, draggable: !disabled })
        .setLngLat(lngLat)
        .addTo(map);
      markerRef.current.on("dragend", () => {
        const p = markerRef.current.getLngLat();
        onChangeRef.current({ latitude: round6(p.lat), longitude: round6(p.lng) });
      });
    } else {
      markerRef.current.setLngLat(lngLat);
    }
  }, [value, disabled]);

  const flyTo = (pin) => {
    mapRef.current?.flyTo({
      center: [pin.longitude, pin.latitude],
      zoom: Math.max(mapRef.current.getZoom(), PIN_ZOOM),
    });
  };

  const applyCoordsText = () => {
    if (!coordsText.trim()) {
      onChange(null);
      return;
    }
    const pin = parseCoordinates(coordsText);
    if (!pin) {
      setCoordsError('Enter "latitude, longitude", e.g. 22.3039, 70.8022');
      return;
    }
    onChange(pin);
    flyTo(pin);
  };

  const findPlace = async () => {
    const q = query.trim();
    if (!q) return;
    // Coordinates typed into the search box are taken as they are.
    const typed = parseCoordinates(q);
    if (typed) {
      onChange(typed);
      flyTo(typed);
      setResults([]);
      return;
    }

    setSearching(true);
    setSearchError("");
    setResults([]);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=in&q=${encodeURIComponent(q)}`
      );
      const rows = await response.json();
      if (!Array.isArray(rows) || rows.length === 0) {
        setSearchError("No place found. Try adding the district or state.");
      } else {
        setResults(rows);
      }
    } catch {
      setSearchError("Place search is unavailable right now. Click the map instead.");
    } finally {
      setSearching(false);
    }
  };

  const chooseResult = (row) => {
    const pin = { latitude: round6(Number(row.lat)), longitude: round6(Number(row.lon)) };
    onChange(pin);
    flyTo(pin);
    setResults([]);
  };

  return (
    <Form.Group className="mt-3">
      <Form.Label className="d-flex align-items-center gap-1 mb-1">
        <MapPin size={14} /> Map pin <span className="text-muted fw-normal">(optional)</span>
      </Form.Label>
      <p className="text-muted mb-2" style={{ fontSize: "12px" }}>
        GIS search uses this to take people here when the location has no maps yet.
        {!disabled && " Find the place, click the map, or paste coordinates."}
      </p>

      {!disabled && (
        <div className="position-relative mb-2">
          <div className="d-flex gap-2">
            <Form.Control
              size="sm"
              type="text"
              value={query}
              placeholder="Find a place, e.g. Bardoli, Surat"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  findPlace();
                }
              }}
            />
            <Button
              size="sm"
              variant="outline-primary"
              className="d-flex align-items-center gap-1 text-nowrap"
              onClick={findPlace}
              disabled={searching || !query.trim()}
            >
              <Search size={13} />
              {searching ? "Finding…" : "Find"}
            </Button>
          </div>
          {searchError && (
            <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
              {searchError}
            </div>
          )}
          {results.length > 0 && (
            <ListGroup
              className="position-absolute w-100 shadow-sm mt-1"
              style={{ zIndex: 5, maxHeight: "190px", overflowY: "auto" }}
            >
              {results.map((row) => (
                <ListGroup.Item
                  key={row.place_id}
                  action
                  className="py-1.5"
                  style={{ fontSize: "12px" }}
                  onClick={() => chooseResult(row)}
                >
                  {row.display_name}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        className="rounded border"
        style={{ height: "240px", width: "100%" }}
        aria-label="Map: click to place the location's pin"
      />

      <div className="d-flex gap-2 mt-2 align-items-start">
        <div className="flex-grow-1">
          <Form.Control
            size="sm"
            type="text"
            value={coordsText}
            placeholder="Latitude, longitude"
            disabled={disabled}
            isInvalid={Boolean(coordsError)}
            onChange={(e) => {
              setCoordsText(e.target.value);
              setCoordsError("");
            }}
            onBlur={applyCoordsText}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyCoordsText();
              }
            }}
          />
          <Form.Control.Feedback type="invalid">{coordsError}</Form.Control.Feedback>
        </div>
        {!disabled && value && (
          <Button
            size="sm"
            variant="outline-secondary"
            className="d-flex align-items-center gap-1 text-nowrap"
            onClick={() => onChange(null)}
            title="Remove the pin"
          >
            <X size={13} /> Clear
          </Button>
        )}
      </div>
    </Form.Group>
  );
}
